<template>
  <v-container fluid class="pa-4">
    <v-card class="elevation-2">
      <v-card-title class="headline">Lease Creation & Excel Import</v-card-title>

      <!-- Excel Import Section -->
      <v-card class="mb-6" color="grey lighten-4">
        <v-card-title class="subtitle-1 font-weight-bold">
          Import Leases from Excel
        </v-card-title>
        <v-card-text>
          <v-file-input
            v-model="excelFile"
            label="Upload Excel File (.xlsx)"
            accept=".xlsx,.xls"
            outlined dense
            prepend-icon="mdi-microsoft-excel"
            @change="handleFileUpload"
          />
          <v-btn
            color="green darken-2"
            dark
            large
            @click="previewImport"
            :disabled="!parsedLeases.length"
            :loading="parsing"
            class="mt-3"
          >
            Preview & Import ({{ parsedLeases.length }} rows)
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Manual Steps -->
      <v-stepper v-model="step" class="mb-6">
        <v-stepper-header>
          <v-stepper-step v-for="n in 4" :key="n" :complete="step > n" :step="n" color="green">
            Step {{ n }}
          </v-stepper-step>
        </v-stepper-header>
        <v-stepper-items>
          <v-stepper-content :step="1">
            <property-selector :properties="propertyList" @property-selected="selectedProperty = $event" />
          </v-stepper-content>
          <v-stepper-content :step="2">
            <v-select 
              :items="tenants" 
              item-text="name" 
              item-value="id" 
              label="Tenant" 
              v-model="selectedTenant" 
              clearable 
              outlined 
              dense 
            />
          </v-stepper-content>
          <v-stepper-content :step="3">
            <lease-details @leaseDetailsSubmitted="leaseDetails = $event" />
          </v-stepper-content>
          <v-stepper-content :step="4">
            <v-form ref="manualForm" v-model="formValid">
              <v-text-field 
                v-model="newLeaseRef"
                label="Lease Reference *"
                outlined 
                dense
                :rules="[v => !!v?.trim() || 'Lease reference is required']"
                required
                :loading="generatingRef"
                hint="Auto-generated (LEA######)"
                persistent-hint
              />
              <v-text-field 
                label="Deposit Required" 
                v-model.number="depositDetails.depositRequired" 
                type="number" 
                min="0"
                outlined 
                dense 
              />
              <v-text-field 
                label="Deposit Held" 
                v-model.number="depositDetails.depositHeld" 
                type="number" 
                min="0"
                outlined 
                dense 
              />
            </v-form>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <v-card-actions class="justify-space-between">
        <v-btn color="grey" v-if="step > 1" @click="step--">Previous</v-btn>
        <v-btn 
          color="green" 
          v-if="step < 4" 
          @click="step++" 
          :disabled="isNextButtonDisabled"
        >
          Next
        </v-btn>
        <v-btn 
          color="green darken-2" 
          dark 
          v-if="step === 4" 
          :loading="submitting"
          :disabled="!formValid || submitting || !newLeaseRef?.trim()"
          @click="submitLeaseDetails"
        >
          Submit Manual Lease
        </v-btn>
      </v-card-actions>

      <!-- PREVIEW DIALOG -->
      <v-dialog v-model="previewDialog" max-width="1900" persistent scrollable>
        <v-card>
          <v-card-title class="headline">
            Import Preview
            <v-spacer />
            <v-btn icon @click="previewDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-text>
            <v-row class="mb-4">
              <v-col cols="auto">
                <v-checkbox v-model="selectAll" label="Select All Valid" @change="toggleAll" />
              </v-col>
              <v-col>
                <v-btn
                  color="green"
                  dark
                  large
                  @click="saveToDatabase"
                  :loading="importing"
                  :disabled="autoSelectedCount === 0"
                >
                  Import {{ autoSelectedCount }} Valid Leases
                </v-btn>
              </v-col>
            </v-row>

            <v-data-table
              :headers="previewHeaders"
              :items="previewItems"
              dense
              :items-per-page="250"
              class="elevation-2"
              :item-class="item => item.ready ? '' : 'error--text'"
            >
              <template v-slot:item.select="{ item }">
                <v-checkbox v-model="item.selected" :disabled="!item.ready" />
              </template>
              <template v-slot:item.rentalAmount="{ item }">
                <strong class="green--text">
                  {{ item.rentalAmount > 0 ? 'R ' + item.rentalAmount.toLocaleString('en-ZA', {minimumFractionDigits: 2}) : '—' }}
                </strong>
              </template>
              <template v-slot:item.leaseStart="{ item }">{{ formatDate(item.leaseStart) }}</template>
              <template v-slot:item.leaseExpiryDisplay="{ item }">{{ item.leaseExpiryDisplay }}</template>
              <template v-slot:item.status="{ item }">
                <v-chip :color="item.ready ? 'green' : 'red'" dark small>{{ item.status }}</v-chip>
              </template>
              <template v-slot:item.occupancyPreview="{ item }">
                <v-chip :color="item.occupancyColor" dark small>{{ item.occupancyText }}</v-chip>
              </template>
              <template v-slot:item.tenantPlacement="{ item }">
                <v-chip :color="item.tenantPlacementColor" dark small class="font-weight-bold">
                  {{ item.tenantPlacement }}
                </v-chip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Import Log -->
      <v-dialog v-model="logDialog" max-width="900">
        <v-card>
          <v-card-title>Import Complete</v-card-title>
          <v-card-text class="preformatted">
            <div v-for="(log, i) in importLog" :key="i" :class="log.includes('SUCCESS') || log.includes('Created') ? 'green--text' : 'red--text'">
              {{ log }}
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn color="green" @click="logDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-container>
</template>

<script>
import {
  getDocs,
  collection,
  query,
  orderBy,
  limit,
  where,
  doc,
  setDoc,
  updateDoc,
  writeBatch,
  serverTimestamp,
  getFirestore
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import * as XLSX from "xlsx";
import PropertySelector from "./PropertySelection.vue";
import LeaseDetails from "./LeaseDetails.vue";

const db = getFirestore();
const auth = getAuth();

// Smart fuzzy matching
const cleanName = (str) => {
  if (!str) return "";
  return str.toString().toLowerCase()
    .replace(/\(.*?\)/g, "")
    .replace(/[^\w\s]/g, " ")
    .replace(/\b(pty|ltd|properties|prop|cc|inc|limited|trading)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
};
const fuzzyIncludes = (a, b) => cleanName(a).includes(cleanName(b)) || cleanName(b).includes(cleanName(a));

export default {
  components: { PropertySelector, LeaseDetails },
  data() {
    return {
      step: 1,
      formValid: false,
      submitting: false,
      generatingRef: false,

      propertyList: [], tenants: [], selectedProperty: null, selectedTenant: null,
      leaseDetails: null, depositDetails: { depositRequired: 0, depositHeld: 0 },
      newLeaseRef: "", excelFile: null, parsedLeases: [], parsing: false,
      previewDialog: false, previewItems: [], selectAll: false,
      importing: false, importLog: [], logDialog: false,

      previewHeaders: [
        { text: "Select", value: "select", width: 80 },
        { text: "REF", value: "leaseRef", width: 120 },
        { text: "Tenant", value: "tenantName", width: 240 },
        { text: "Placement", value: "tenantPlacement", width: 140 },
        { text: "Property", value: "propertyName", width: 180 },
        { text: "Unit", value: "address", width: 160 },
        { text: "Start", value: "leaseStart", width: 110 },
        { text: "Rental", value: "rentalAmount", width: 130, align: "end" },
        { text: "Expiry", value: "leaseExpiryDisplay", width: 110 },
        { text: "Status", value: "status", width: 280 },
        { text: "Occupancy", value: "occupancyPreview", width: 300 },
      ],
    };
  },

  computed: {
    isNextButtonDisabled() {
      return (this.step === 1 && !this.selectedProperty) ||
             (this.step === 2 && !this.selectedTenant) ||
             (this.step === 3 && !this.leaseDetails) ||
             (this.step === 4 && !this.formValid);
    },
    autoSelectedCount() {
      return this.previewItems.filter(i => i.ready && i.selected).length;
    },
  },

  watch: {
    step(newStep) {
      if (newStep === 4 && !this.newLeaseRef) {
        this.generateLeaseReference();
      }
    },
    previewItems: {
      handler() {
        const ready = this.previewItems.filter(i => i.ready);
        this.selectAll = ready.length > 0 && ready.every(i => i.selected);
      },
      deep: true
    }
  },

  methods: {
    async generateLeaseReference() {
      this.generatingRef = true;
      this.newLeaseRef = "LEA000001"; // fallback

      try {
        const leasesRef = collection(db, "Leases");
        const q = query(
          leasesRef,
          orderBy("leaseReference", "desc"),
          limit(1)
        );

        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const lastDoc = snapshot.docs[0].data();
          const lastRef = lastDoc.leaseReference || "";

          if (lastRef.startsWith("LEA")) {
            const numPart = lastRef.replace("LEA", "");
            const lastNumber = parseInt(numPart, 10);
            if (!isNaN(lastNumber)) {
              const nextNumber = lastNumber + 1;
              this.newLeaseRef = "LEA" + nextNumber.toString().padStart(6, "0");
            }
          }
        }
      } catch (err) {
        console.error("Could not generate lease reference:", err);
      } finally {
        this.generatingRef = false;
      }
    },

    async submitLeaseDetails() {
      if (!this.$refs.manualForm.validate()) return;

      this.submitting = true;

      try {
        const tenant = this.tenants.find(t => t.id === this.selectedTenant);
        if (!tenant) throw new Error("Selected tenant not found");

        const startDateObj = this.parseDate(this.leaseDetails?.startDate);
        if (!startDateObj) throw new Error("Invalid or missing lease start date");

        const endDateObj = this.leaseDetails?.endDate ? this.parseDate(this.leaseDetails.endDate) : null;
        const isMtM = !endDateObj || /mtm|month to month/i.test(this.leaseDetails?.type || "");

        const leaseData = {
          leaseReference: this.newLeaseRef.trim().toUpperCase(),
          propertyId: this.selectedProperty.id,
          propertyName: this.selectedProperty.displayName || this.selectedProperty.name || "—",
          address: this.selectedProperty.displayName || this.selectedProperty.name || "—",
          tenantId: tenant.id,
          tenantName: tenant.name,
          leaseDetails: {
            startDate: startDateObj.toISOString().split("T")[0],
            endDate: endDateObj ? endDateObj.toISOString().split("T")[0] : null,
            rentalAmount: Number(this.leaseDetails?.rentalAmount || 0).toFixed(2),
            prorataAmount: isMtM ? Number(this.leaseDetails?.rentalAmount || 0).toFixed(2) : "0.00",
            type: isMtM ? "Month to Month Lease" : "Fixed Term",
            invoiceDate: "1st of each Month",
            dueDay: "1st of each Month",
            rentDueFrequency: "1st of each Month",
          },
          depositRequired: Number(this.depositDetails.depositRequired || 0),
          depositHeld: Number(this.depositDetails.depositHeld || 0),
          createdAt: serverTimestamp(),
          userUid: auth.currentUser?.uid || null,
          status: "active"
        };

        // Final duplicate check
        const existingQuery = query(
          collection(db, "Leases"),
          where("leaseReference", "==", leaseData.leaseReference)
        );
        const existing = await getDocs(existingQuery);
        if (!existing.empty) {
          throw new Error("Lease reference already exists – please try again");
        }

        const leaseDoc = doc(collection(db, "Leases"));
        await setDoc(leaseDoc, leaseData);

        if (tenant.ref && !tenant.placed) {
          await updateDoc(tenant.ref, {
            placed: true,
            placedAt: new Date().toISOString()
          });
        }

        alert(`Lease ${leaseData.leaseReference} created successfully!`);

        this.newLeaseRef = "";
        this.depositDetails = { depositRequired: 0, depositHeld: 0 };
        this.selectedProperty = null;
        this.selectedTenant = null;
        this.leaseDetails = null;
        this.$refs.manualForm.resetValidation();
        this.step = 1;

      } catch (err) {
        console.error(err);
        alert("Failed to create lease:\n" + err.message);
      } finally {
        this.submitting = false;
      }
    },

    parseDate(val) {
      if (!val) return null;
      const str = String(val).trim();
      const d = new Date(str);
      if (!isNaN(d)) return d;
      const m = str.match(/(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})/);
      if (m) return new Date(`${m[3]}-${m[2].padStart(2,"0")}-${m[1].padStart(2,"0")}`);
      return null;
    },
    formatDate(val) {
      const d = this.parseDate(val);
      return d ? d.toLocaleDateString("en-ZA") : "(none)";
    },

    async fetchProperties() {
      const snap = await getDocs(collection(db, "properties"));
      this.propertyList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    },
    async fetchTenants() {
      const snap = await getDocs(collection(db, "tenants"));
      this.tenants = snap.docs.map(d => {
        const data = d.data();
        return { id: data.reference || d.id, name: data.displayAs || "Unnamed" };
      });
    },

    handleFileUpload() {
      this.parsedLeases = [];
      if (!this.excelFile) return;
      this.parsing = true;
      const reader = new FileReader();
      reader.onload = e => {
        const wb = XLSX.read(e.target.result, { type: "array" });
        this.parsedLeases = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
        this.parsing = false;
      };
      reader.readAsArrayBuffer(this.excelFile);
    },

    async previewImport() {
      if (!this.parsedLeases.length) return;

      this.previewDialog = true;
      this.previewItems = [];

      const existingRefs = new Set();
      (await getDocs(collection(db, "Leases"))).forEach(d => {
        const ref = d.data().leaseReference?.trim().toUpperCase();
        if (ref) existingRefs.add(ref);
      });

      const [tenantSnap, propSnap, unitSnap] = await Promise.all([
        getDocs(collection(db, "tenants")),
        getDocs(collection(db, "properties")),
        getDocs(collection(db, "units"))
      ]);

      const tenantsList = tenantSnap.docs.map(d => ({
        id: d.data().reference || d.id,
        name: d.data().displayAs,
        ref: d.ref,
        placed: d.data().placed === true
      }));
      const propertiesList = propSnap.docs.map(d => ({ id: d.id, name: d.data().displayName, ref: d.ref }));

      const unitsByProperty = {};
      unitSnap.forEach(d => {
        const u = d.data();
        if (!unitsByProperty[u.propertyId]) unitsByProperty[u.propertyId] = [];
        unitsByProperty[u.propertyId].push({ name: u.unitName, ref: d.ref });
      });

      const items = this.parsedLeases.map((row, idx) => {
        const ref = (row["REFERENCE"] || "").toString().trim().toUpperCase();
        const tenantRaw = (row["Tenant Name"] || "").toString().trim();
        const propertyRaw = (row["Property"] || "").toString().trim();
        const unitRaw = (row["Unit"] || "").toString().trim();

        let rental = 0;
        const rentalKey = Object.keys(row).find(k => /rental|amount|monthly|rent/i.test(k));
        if (rentalKey && row[rentalKey]) {
          const n = parseFloat(String(row[rentalKey]).replace(/[^0-9.-]/g, ""));
          if (!isNaN(n) && n > 0) rental = n;
        }

        return {
          index: idx,
          rawLeaseRef: ref,
          tenantRaw, propertyRaw, unitRaw,
          address: unitRaw ? `${propertyRaw} - Unit ${unitRaw}` : propertyRaw,
          leaseStart: row["Lease Start"] || "",
          leaseExpiry: row["Lease Expiry"] || "",
          leaseType: (row["Lease Type"] || "").toString().trim(),
          rentalAmount: rental,
          depositRequired: parseFloat(String(row["Deposit Required"] || 0).replace(/[^0-9.-]/g, "")) || 0,
          depositHeld: parseFloat(String(row["Deposit Held"] || 0).replace(/[^0-9.-]/g, "")) || 0,
        };
      });

      this.previewItems = items.map(item => {
        const warnings = [];
        if (!item.rawLeaseRef) warnings.push("missing REF");
        if (existingRefs.has(item.rawLeaseRef)) warnings.push("REF exists");
        if (!this.parseDate(item.leaseStart)) warnings.push("bad start date");

        const tenant = tenantsList.find(t => fuzzyIncludes(t.name, item.tenantRaw)) || null;
        const property = propertiesList.find(p => fuzzyIncludes(p.name, item.propertyRaw)) || null;

        if (!tenant) warnings.push("tenant not found");
        if (!property) warnings.push("property not found");

        const isMtM = /mtm|month.*month|expired/i.test(item.leaseType) ||
                      !item.leaseExpiry || String(item.leaseExpiry).toLowerCase() === "mtm";

        const ready = warnings.length === 0;
        const status = ready ? "Ready" : "Invalid: " + warnings.join(", ");

        let tenantPlacement = "—";
        let tenantPlacementColor = "grey";
        if (!tenant) {
          tenantPlacement = "Not found";
          tenantPlacementColor = "red";
        } else if (tenant.placed) {
          tenantPlacement = "Already placed";
          tenantPlacementColor = "green";
        } else {
          tenantPlacement = "Will be placed";
          tenantPlacementColor = "blue";
        }

        let occupancyText = "No change";
        let occupancyColor = "grey";
        let willCreateUnit = false;
        let unitRefToOccupy = null;

        if (property && item.unitRaw) {
          const target = `Unit ${item.unitRaw}`.replace(/unit\s+/gi, "Unit ");
          const existing = unitsByProperty[property.id]?.find(u => fuzzyIncludes(u.name, target));
          if (existing) {
            occupancyText = `Will occupy ${existing.name}`;
            occupancyColor = "green";
            unitRefToOccupy = existing.ref;
          } else {
            occupancyText = `Will create & occupy Unit ${item.unitRaw}`;
            occupancyColor = "green";
            willCreateUnit = true;
          }
        }

        return {
          ...item,
          leaseRef: item.rawLeaseRef,
          tenantId: tenant?.id || null,
          tenantName: tenant?.name || item.tenantRaw,
          propertyId: property?.id || null,
          propertyName: property?.name || item.propertyRaw,
          leaseExpiryDisplay: isMtM ? "MtM" : this.formatDate(item.leaseExpiry),
          status, ready, selected: ready, isMtM,
          tenantPlacement, tenantPlacementColor,
          occupancyText, occupancyColor,
          willCreateUnit, unitRefToOccupy,
          propertyRef: property?.ref,
          tenantRef: tenant?.ref,
          tenantAlreadyPlaced: tenant?.placed === true
        };
      });

      this.selectAll = this.previewItems.some(i => i.ready);
    },

    toggleAll() {
      this.previewItems.forEach(i => { if (i.ready) i.selected = this.selectAll; });
    },

    async saveToDatabase() {
      const toImport = this.previewItems.filter(i => i.ready && i.selected);
      this.importing = true;
      this.importLog = [`Importing ${toImport.length} leases...`];
      const batch = writeBatch(db);

      for (const item of toImport) {
        const log = `${item.leaseRef} → ${item.tenantName} @ ${item.propertyName}${item.unitRaw ? ' Unit ' + item.unitRaw : ''}`;

        try {
          const startDate = this.parseDate(item.leaseStart).toISOString().split("T")[0];
          const endDate = item.isMtM ? null : (this.parseDate(item.leaseExpiry)?.toISOString().split("T")[0] || null);

          const leaseDoc = doc(collection(db, "Leases"));
          batch.set(leaseDoc, {
            leaseReference: item.leaseRef,
            propertyId: item.propertyId,
            propertyName: item.propertyName,
            address: item.address,
            tenantId: item.tenantId,
            tenantName: item.tenantName,
            leaseDetails: {
              startDate,
              endDate,
              rentalAmount: item.rentalAmount.toFixed(2),
              prorataAmount: item.isMtM ? item.rentalAmount.toFixed(2) : "0.00",
              type: item.isMtM ? "Month to Month Lease" : "Fixed Term",
              invoiceDate: "1st of each Month",
              dueDay: "1st of each Month",
              rentDueFrequency: "1st of each Month",
            },
            depositRequired: item.depositRequired,
            depositHeld: item.depositHeld,
            createdAt: serverTimestamp(),
            userUid: auth.currentUser.uid,
          });

          if (item.willCreateUnit && item.propertyId && item.unitRaw) {
            const unitName = `Unit ${item.unitRaw}`.replace(/unit\s+/gi, "Unit ");
            const unitDoc = doc(collection(db, "units"));
            batch.set(unitDoc, {
              propertyId: item.propertyId,
              unitName,
              isOccupied: true,
              occupiedAt: new Date().toISOString(),
              createdAt: serverTimestamp(),
            });
            this.importLog.push(`${log} → Created Unit ${item.unitRaw}`);
          }

          if (item.unitRefToOccupy) {
            batch.update(item.unitRefToOccupy, {
              isOccupied: true,
              occupiedAt: new Date().toISOString(),
            });
          }

          if (item.tenantRef && !item.tenantAlreadyPlaced) {
            batch.update(item.tenantRef, {
              placed: true,
              placedAt: new Date().toISOString()
            });
          }

          this.importLog.push(`${log} → SUCCESS`);
        } catch (err) {
          this.importLog.push(`${log} → FAILED: ${err.message}`);
        }
      }

      await batch.commit();
      this.importing = false;
      this.importLog.push("IMPORT COMPLETE!");
      this.logDialog = true;
      this.previewDialog = false;
    },
  },

  mounted() {
    this.fetchProperties();
    this.fetchTenants();
  },
};
</script>

<style scoped>
.preformatted { font-family: monospace; white-space: pre-line; }
.error--text { background-color: #ffebee !important; }
</style>